import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { DynamicIcon } from 'phosphor-icons/react';
import { forwardRef } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2Props } from './Button.v2.types';

export const buttonAnimation = `relative overflow-hidden
  before:content-[''] before:absolute before:inset-y-0 before:left-0 before:h-full before:w-0 before:pointer-events-none before:transition-[width] before:duration-300 before:ease-linear
  hover:before:w-full
  before:rounded-inherit
  *:relative
  motion-reduce:before:transition-none`;

export const buttonVariants = cva(
  `inline-flex items-center justify-center whitespace-nowrap select-none ring-offset-background transition-colors
  focus-visible:outline-none
  disabled:pointer-events-none`,
  {
    variants: {
      variant: {
        primary: `
          font-bold text-accent-content uppercase bg-accent border-2 border-accent
          hover:bg-accent-hover hover:text-grey-100
          active:bg-accent-hover active:ring-2 active:ring-blue dark:active:ring-focus active:ring-offset-1 active:ring-offset-canvas active:text-grey-100
          focus:bg-accent focus:ring-2 focus:ring-blue dark:focus:ring-focus focus:ring-offset-1 focus:ring-offset-canvas focus:text-grey-100
          disabled:bg-grey-400 disabled:border-grey-400`,
        'primary-red-monochrome': `
          font-bold text-white uppercase bg-red-600 border-2 border-red-600
          hover:bg-red-500
          active:bg-red-500 active:ring-2 active:ring-red-500 active:ring-offset-1 active:ring-offset-canvas
          focus:bg-red-600 focus:ring-2 focus:ring-blue dark:focus:ring-focus focus:ring-offset-1 focus:ring-offset-canvas
          disabled:bg-grey-400 disabled:border-grey-400`,
        secondary: `
          bg-transparent font-bold text-grey-100 uppercase border-2 border-blue
          hover:bg-blue-hover hover:text-white
          active:bg-transparent active:border-blue active:ring-2 active:ring-blue active:ring-offset-2 active:ring-offset-black
          focus-visible:bg-transparent focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-black
          disabled:border-grey-400 disabled:text-grey-400 disabled:bg-transparent
          ${buttonAnimation} before:bg-blue-hover
        `,
        'secondary-blue-monochrome': `
          font-bold uppercase bg-surface text-link border-2 border-accent
          hover:bg-accent hover:text-accent-content
          active:text-accent-content active:bg-accent active:ring-2 active:ring-blue dark:active:ring-focus active:ring-offset-1 active:ring-offset-canvas
          focus:ring-2 focus:ring-offset-1 focus:ring-offset-canvas focus:ring-blue dark:focus:ring-focus
          disabled:border-[#999] disabled:text-[#999] disabled:bg-surface
          ${buttonAnimation} before:bg-accent
        `,
        'secondary-red-monochrome': `
          font-bold uppercase bg-surface text-danger border-2 border-danger
          hover:bg-red-500 hover:text-white
          active:text-white active:bg-red-500 active:ring-2 active:ring-red-500 active:ring-offset-1 active:ring-offset-canvas
          focus:ring-2 focus:ring-offset-1 focus:ring-offset-canvas focus:ring-blue dark:focus:ring-focus
          disabled:border-[#999] disabled:text-[#999] disabled:bg-surface
          ${buttonAnimation} before:bg-red-500
        `,
        'secondary-black-monochrome': `
          font-bold uppercase bg-surface text-grey-800 dark:text-content border-2 border-grey-800 dark:border-content
          hover:bg-grey-800 dark:hover:bg-surface-inverse hover:text-content-inverse dark:hover:text-content-inverse
          active:text-content-inverse dark:active:text-content-inverse active:bg-grey-800 dark:active:bg-surface-inverse active:ring-2 active:ring-grey-800 dark:active:ring-content active:ring-offset-1 active:ring-offset-canvas
          focus:bg-surface focus:ring-2 focus:ring-offset-1 focus:ring-offset-canvas focus:ring-grey-800 dark:focus:ring-content
          disabled:border-0 disabled:text-[#999] dark:disabled:text-[#999] disabled:bg-surface
          ${buttonAnimation} before:bg-grey-800 dark:before:bg-surface-inverse
        `,
        tertiary: `
          bg-transparent text-white font-bold uppercase border-2 border-transparent
          hover:bg-white hover:text-black
          active:text-black active:bg-white active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-black
          focus:text-white focus:bg-transparent focus:border-blue
          disabled:text-[#999] disabled:bg-transparent`,
        'tertiary-black-monochrome': `
          bg-transparent text-grey-800 dark:text-content font-bold uppercase border-2 border-transparent
          hover:bg-grey-800 dark:hover:bg-surface-inverse hover:text-content-inverse dark:hover:text-content-inverse
          active:text-content-inverse dark:active:text-content-inverse active:bg-grey-800 dark:active:bg-surface-inverse active:ring-2 active:ring-grey-800 dark:active:ring-content active:ring-offset-1 active:ring-offset-canvas
          focus:text-grey-800 dark:focus:text-content focus:bg-transparent dark:focus:bg-transparent focus:outline focus:outline-2 focus:!outline-blue dark:focus:!outline-focus focus:outline-offset-2
          disabled:text-[#999] dark:disabled:text-[#999] disabled:bg-transparent`,
        'tertiary-blue-monochrome': `
          bg-transparent text-link font-medium border-2 border-transparent
          hover:bg-surface-muted
          active:bg-surface-muted
          focus:border-blue dark:focus:border-focus focus:outline-offset-2
          disabled:text-grey-400 disabled:bg-transparent`,
        'tertiary-red-monochrome': `
          bg-transparent text-danger font-medium border-2 border-transparent
          hover:bg-surface-muted
          active:bg-surface-muted
          focus:border-blue dark:focus:border-focus focus:outline-offset-2
          disabled:text-grey-400 disabled:bg-transparent`,
        'tertiary-grey-monochrome': `
          bg-transparent text-content-subtle font-medium border-2 border-transparent
          hover:bg-surface-muted
          active:bg-surface-muted active:text-content-muted
          focus-visible:outline focus-visible:outline-2 focus-visible:!outline-blue dark:focus-visible:!outline-focus focus-visible:outline-offset-0
          disabled:text-grey-400 disabled:bg-transparent`,
        'dark-secondary': `
          bg-transparent text-white border-2 border-blue font-bold uppercase
          hover:bg-white hover:text-grey-900 hover:border-blue
          active:text-white active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-black
          focus:text-white focus:bg-transparent focus:border-blue
          disabled:bg-transparent disabled:text-grey-700 disabled:border-grey-700
          `,
        'dark-tertiary': `
          bg-transparent text-white font-bold uppercase
          border-2 border-transparent
          hover:bg-grey-700
          active:bg-grey-700 active:ring-2 active:ring-blue active:ring-offset-2 active:ring-offset-grey-900
          focus:bg-transparent focus:ring-2 focus:ring-blue focus:ring-offset-2 focus:ring-offset-grey-900
          focus-visible:outline-none focus-visible:border-0
          disabled:text-grey-700 disabled:bg-transparent
        `,
      },
      size: {
        large: 'py-[11px] px-[22px] text-base tracking-[2.88px]',
        medium: 'py-2.5 px-3.5 text-sm tracking-[2.88px]',
        small: 'py-2 px-4 text-sm tracking-[2.52px]',
        xsmall: 'py-1.5 px-1.5 text-[13px] tracking-[-0.039px]',
      },
      shape: {
        rounded: 'rounded-full',
        square: 'rounded-lg',
      },
      iconOnly: {
        true: '',
        false: '[&_svg]:mx-0.5',
      },
      hasLeftIcon: {
        true: '',
        false: '',
      },
      hasRightIcon: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'large',
      shape: 'rounded',
      iconOnly: false,
    },
    compoundVariants: [
      // Icon only variants for different sizes
      {
        size: 'large',
        iconOnly: true,
        className: 'h-12 w-12',
      },
      {
        size: 'medium',
        iconOnly: true,
        className: 'h-10 w-10',
      },
      {
        size: 'small',
        iconOnly: true,
        className: 'h-9 w-9',
      },
      {
        size: 'xsmall',
        iconOnly: true,
        className: 'h-8 w-8',
      },
      // Small size with rounded shape padding
      {
        size: 'small',
        shape: 'rounded',
        iconOnly: false,
        className: 'px-[22px]',
      },
      // padding overrides text & icon (left or right)
      {
        size: 'large',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-3',
      },
      {
        size: 'large',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-3',
      },
      {
        size: 'medium',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-2',
      },
      {
        size: 'medium',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-2',
      },
      {
        size: 'small',
        shape: 'rounded',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-3',
      },
      {
        size: 'small',
        shape: 'rounded',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-3',
      },
      {
        size: 'small',
        shape: 'square',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-2 pr-3.5',
      },
      {
        size: 'small',
        shape: 'square',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-2 pr-3.5',
      },
      {
        variant: 'tertiary-grey-monochrome',
        size: 'xsmall',
        className: 'rounded leading-5',
      },
    ],
  }
);

export const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      className,
      variant = 'primary',
      size = 'large',
      shape = 'rounded',
      label = '',
      children,
      iconLeft,
      iconRight,
      iconClassName,
      iconWeight = 'regular',
      iconSize = 16,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const hasContent = children || (label && label.trim().length > 0);
    const isIconOnly = !hasContent;

    const hasLeftIcon =
      typeof iconLeft === 'string'
        ? iconLeft.trim().length > 0
        : Boolean(iconLeft);
    const hasRightIcon =
      typeof iconRight === 'string'
        ? iconRight.trim().length > 0
        : Boolean(iconRight);

    const hasAnyIcon = hasLeftIcon || hasRightIcon;
    const textGapClass = !isIconOnly && hasAnyIcon ? 'gap-x-0.5' : '';

    const Component = asChild ? Slot : 'button';
    const Content = asChild ? Slottable : 'span';

    return isIconOnly ? (
      <Component
        data-test={`${label.toLowerCase().replace(/\s+/g, '-')}-button`}
        ref={ref}
        className={c(
          buttonVariants({
            variant,
            size,
            shape,
            iconOnly: isIconOnly,
            hasLeftIcon,
            hasRightIcon,
          }),
          textGapClass,
          className
        )}
        {...props}
      >
        <span>
          <DynamicIcon
            iconName={(iconLeft as string) || (iconRight as string)}
            className={c(iconClassName)}
            weight={iconWeight}
            size={iconSize}
          />
        </span>
      </Component>
    ) : (
      <Component
        data-test={`${label.toLowerCase().replace(/\s+/g, '-')}-button`}
        ref={ref}
        className={c(
          buttonVariants({
            variant,
            size,
            shape,
            iconOnly: isIconOnly,
            hasLeftIcon,
            hasRightIcon,
          }),
          textGapClass,
          className
        )}
        {...props}
      >
        <DynamicIcon
          iconName={iconLeft as string}
          className={c(iconClassName, { hidden: !hasLeftIcon })}
          weight={iconWeight}
          size={iconSize}
        />

        <Content>{children || label}</Content>

        <DynamicIcon
          iconName={iconRight as string}
          className={c(iconClassName, { hidden: !hasRightIcon })}
          weight={iconWeight}
          size={iconSize}
        />
      </Component>
    );
  }
);

ButtonV2.displayName = 'ButtonV2';
